<template>
  <div class="settings-form">
    <div v-for="definition in definitions" :key="definition.key" class="settings-form__row">
      <div class="settings-form__meta">
        <label class="settings-form__label" :for="`setting-${uid}-${definition.key}`">{{ label(definition.key) }}</label>
        <div v-if="hint(definition.key)" class="settings-form__hint">{{ hint(definition.key) }}</div>
      </div>

      <div class="settings-form__control">
        <!-- Read only: the values of a group shown on its satellite -->
        <div v-if="readonly" class="settings-form__value">{{ format(valueOf(definition)) }}</div>

        <div v-else-if="definition.type === 'enum'" class="settings-form__segments" role="radiogroup">
          <button
            v-for="choice in definition.constraints.choices"
            :key="choice"
            type="button"
            role="radio"
            :aria-checked="valueOf(definition) === choice"
            class="settings-form__segment"
            :class="{ _active: valueOf(definition) === choice }"
            :disabled="disabled"
            @click="set(definition.key, choice)"
          >
            {{ choice }}
          </button>
        </div>

        <label v-else-if="definition.type === 'bool'" class="settings-form__switch">
          <input :id="`setting-${uid}-${definition.key}`" type="checkbox" :checked="valueOf(definition)" :disabled="disabled" @change="set(definition.key, $event.target.checked)" />
          <span class="settings-form__switch-track"></span>
          <span>{{ format(valueOf(definition)) }}</span>
        </label>

        <input
          v-else-if="definition.type === 'int'"
          :id="`setting-${uid}-${definition.key}`"
          type="number"
          step="1"
          :min="definition.constraints.min"
          :max="definition.constraints.max"
          :value="valueOf(definition)"
          :disabled="disabled"
          :class="{ invalid: !!errorOf(definition) }"
          @input="setInt(definition.key, $event.target.value)"
        />

        <textarea
          v-else-if="definition.type === 'string_list'"
          :id="`setting-${uid}-${definition.key}`"
          rows="4"
          :value="(valueOf(definition) || []).join('\n')"
          :placeholder="$t('satellites.settings.list_placeholder')"
          :disabled="disabled"
          :class="{ invalid: !!errorOf(definition) }"
          @input="setList(definition.key, $event.target.value)"
        ></textarea>

        <input
          v-else
          :id="`setting-${uid}-${definition.key}`"
          type="text"
          :value="valueOf(definition)"
          :disabled="disabled"
          :class="{ invalid: !!errorOf(definition) }"
          @input="set(definition.key, $event.target.value)"
        />

        <div v-if="errorOf(definition)" class="sat-field__error">{{ errorOf(definition) }}</div>
        <div v-else-if="!readonly && isChanged(definition)" class="settings-form__default">
          {{ $t("satellites.settings.default_value", { value: format(definition.default) }) }}
          <button type="button" class="settings-form__reset" :disabled="disabled" @click="set(definition.key, definition.default)">{{ $t("satellites.settings.reset") }}</button>
        </div>
        <div v-else-if="!readonly && definition.type === 'int' && definition.constraints.min !== undefined" class="settings-form__default">
          {{ $t("satellites.settings.range", { min: definition.constraints.min, max: definition.constraints.max }) }}
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { isEqual } from "lodash";
import { formatSettingValue, settingHint, settingLabel, validateSetting } from "@/components/Satellites/settingsSchema";

let uid = 0;

/**
 * Plain form of satellite settings built from the schema. v-model is { key: value } with every setting; the owner
 * (a group, or a satellite without a group) saves the whole object. `readonly` shows the values only.
 */
export default {
  name: "SettingsForm",
  props: {
    definitions: {
      type: Array,
      required: true,
    },
    value: {
      type: Object,
      required: true,
    },
    // Server side violations keyed by "settings.{key}"
    violations: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { uid: ++uid };
  },
  methods: {
    label(key) {
      return settingLabel(this, key);
    },
    hint(key) {
      return settingHint(this, key);
    },
    format(value) {
      return formatSettingValue(this, value);
    },
    valueOf(definition) {
      return definition.key in this.value ? this.value[definition.key] : definition.default;
    },
    isChanged(definition) {
      return !isEqual(this.valueOf(definition), definition.default);
    },
    errorOf(definition) {
      const server = this.violations[`settings.${definition.key}`];
      if (server && server.length) return server.join(" ");
      const error = validateSetting(definition, this.valueOf(definition));
      return error ? this.$t(error[0], error[1]) : "";
    },
    set(key, value) {
      this.$emit("input", { ...this.value, [key]: value });
    },
    setInt(key, raw) {
      const number = Number(raw);
      this.set(key, raw !== "" && Number.isFinite(number) ? number : raw);
    },
    setList(key, text) {
      this.set(
        key,
        text
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter((line) => line.length > 0)
      );
    },
  },
};
</script>
<style lang="scss" scoped>
.settings-form__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  gap: 8px 32px;
  padding: 16px 0;

  & + & {
    border-top: 1px solid var(--sat-border);
  }

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.settings-form__label {
  display: block;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--sat-text);
}

.settings-form__hint {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.45;
  color: var(--sat-muted);
}

.settings-form__control {
  min-width: 0;

  input[type="number"],
  input[type="text"],
  textarea {
    width: 100%;
    max-width: 320px;
  }

  textarea {
    max-width: none;
    resize: vertical;
  }
}

.settings-form__value {
  font-size: 14px;
  font-weight: 600;
  color: var(--sat-text);
  word-break: break-word;
}

.settings-form__segments {
  display: inline-flex;
  flex-wrap: wrap;
  padding: 3px;
  border-radius: 10px;
  background: var(--sat-input);
}

.settings-form__segment {
  min-width: 56px;
  padding: 7px 12px;
  border: 0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--sat-text-2);
  background: transparent;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, box-shadow 0.15s;

  &:hover:not(:disabled):not(._active) {
    color: var(--sat-text);
  }

  &._active {
    color: var(--sat-accent);
    background: var(--sat-card);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--sat-accent);
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
}

.settings-form__switch {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 14px;
  cursor: pointer;

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
}

.settings-form__switch-track {
  position: relative;
  width: 38px;
  height: 22px;
  border-radius: 11px;
  background: var(--sat-border);
  transition: background 0.15s;

  &::after {
    content: "";
    position: absolute;
    top: 3px;
    left: 3px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.15s;
  }

  input:checked + & {
    background: var(--sat-accent);

    &::after {
      transform: translateX(16px);
    }
  }
}

.settings-form__default {
  margin-top: 6px;
  font-size: 12px;
  color: var(--sat-muted);
}

.settings-form__reset {
  margin-left: 6px;
  padding: 0;
  border: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--sat-accent);
  background: transparent;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus {
    outline: none;
  }
}
</style>
